/**
 * Demo-only Drive folder importer.
 *
 * Uses the existing read-only `listDriveFolder` and `importDriveFile` helpers
 * (no new backend endpoints), but writes the result into the demo store —
 * never into projectStore. That's the isolation contract.
 *
 * Side-effect note: thermal-photo and SRT imports go through `importDriveFile`,
 * which uploads thermal images into R2 (the same R2 path the real importer
 * uses). The demo never registers those R2 URLs into projectStore so they
 * become orphaned — picked up later by the existing orphan-asset scanner. This
 * is acceptable for a demo and called out in `docs/DOCK_MISSION_PLAN.md`.
 */

import { useState } from 'react';
import { useProjectStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import {
  listDriveFolder,
  type DriveFile,
} from '../../services/assetApi';
import {
  categorizeFiles,
  matchPhotoSets,
  matchFlightPaths,
  formatBytes,
  type CategorizedFile,
  type PhotoSet,
  type FlightPathSet,
} from '../../utils/driveImport';
import { importMockMissionData } from '../../utils/mockDriveImporter';
import './DriveMissionImportModalDemo.css';

interface Props {
  onClose: () => void;
}

interface ListResult {
  files: DriveFile[];
  categorized: CategorizedFile[];
  photoSets: PhotoSet[];
  flightPaths: FlightPathSet[];
  truncated: boolean;
  truncationReason?: 'files' | 'depth' | 'folders';
}

export default function DriveMissionImportModalDemo({ onClose }: Props) {
  const siteId = useProjectStore((s) => s.meta?.siteId);
  const setMissionImportedData = useDockDemoStore((s) => s.setMissionImportedData);
  const clearMissionImportedData = useDockDemoStore((s) => s.clearMissionImportedData);
  // The modal always writes into the currently-selected mission's slot.
  // No selected mission → import is disabled (the panel doesn't open this
  // modal in that state anyway, but we belt-and-braces it here too).
  const selectedMissionId = useDockDemoStore((s) => s.selectedMissionId);
  const selectedMission = useDockDemoStore((s) =>
    s.missions.find((m) => m.id === s.selectedMissionId) ?? null,
  );
  const importedPhotosCount = selectedMission?.importedPhotos?.length ?? 0;

  const [folderUrl, setFolderUrl] = useState('');
  const [listing, setListing] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [listResult, setListResult] = useState<ListResult | null>(null);

  const [importing, setImporting] = useState(false);
  const [importStep, setImportStep] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importDone, setImportDone] = useState(false);

  const handleList = async () => {
    if (!folderUrl.trim()) return;
    if (!siteId) {
      setListError('Open a site first — the Drive listing call needs a site context.');
      return;
    }
    setListing(true);
    setListError(null);
    setListResult(null);
    try {
      const res = await listDriveFolder(siteId, folderUrl.trim());
      const categorized = categorizeFiles(res.files);
      setListResult({
        files: res.files,
        categorized,
        photoSets: matchPhotoSets(categorized),
        flightPaths: matchFlightPaths(categorized),
        truncated: res.truncated,
        truncationReason: res.truncationReason,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Drive listing failed';
      setListError(msg);
    } finally {
      setListing(false);
    }
  };

  const handleImport = async () => {
    if (!siteId || !listResult || !selectedMissionId) return;
    setImporting(true);
    setImportError(null);
    setImportDone(false);
    try {
      const result = await importMockMissionData(
        siteId,
        folderUrl.trim(),
        (p) => setImportStep(p.step),
      );
      setMissionImportedData(selectedMissionId, result);
      setImportStep('');
      setImportDone(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      setImportError(msg);
    } finally {
      setImporting(false);
    }
  };

  const totalBytes = listResult?.files.reduce((sum, f) => sum + (parseInt(f.size, 10) || 0), 0) ?? 0;
  const thermalCount = listResult?.categorized.filter((f) => f.category === 'thermal_photo').length ?? 0;
  const visualCount = listResult?.categorized.filter((f) => f.category === 'visual_photo').length ?? 0;
  const zoomCount = listResult?.categorized.filter((f) => f.category === 'zoom_photo').length ?? 0;
  const videoCount = listResult?.categorized.filter((f) => f.category === 'video').length ?? 0;
  const srtCount = listResult?.categorized.filter((f) => f.category === 'srt').length ?? 0;

  return (
    <div className="dock-demo-modal__backdrop" onClick={onClose}>
      <div className="dock-demo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dock-demo-modal__header">
          <span className="dock-demo-modal__title">
            Import mission data
          </span>
          <button className="dock-demo-modal__close" onClick={onClose} type="button">×</button>
        </div>

        <div className="dock-demo-modal__body">
          {!siteId && (
            <div className="dock-demo-banner dock-demo-banner--warn">
              No site is open. Open a site to enable Drive listing.
            </div>
          )}
          {!selectedMissionId && (
            <div className="dock-demo-banner dock-demo-banner--warn">
              Select a mission first — imported data is attached to a mission
              so its visibility checkbox controls the map render.
            </div>
          )}
          {selectedMission && (
            <p className="dock-demo-modal__lead" style={{ marginTop: 0 }}>
              Importing into mission: <strong>{selectedMission.name}</strong>
            </p>
          )}

          <p className="dock-demo-modal__lead">
            Paste a publicly-shared Google Drive folder containing example
            videos, photos, and SRT files. The import populates the demo only —
            nothing is added to the real project.
          </p>

          <label className="dock-demo-label">
            Drive folder URL
            <input
              type="url"
              className="dock-demo-input"
              placeholder="https://drive.google.com/drive/folders/..."
              value={folderUrl}
              onChange={(e) => setFolderUrl(e.target.value)}
              disabled={listing || importing || !siteId || !selectedMissionId}
            />
          </label>

          <div className="dock-demo-modal__row">
            <button
              type="button"
              className="dock-demo-btn"
              onClick={handleList}
              disabled={listing || importing || !folderUrl.trim() || !siteId || !selectedMissionId}
            >
              {listing ? 'Listing…' : 'List files'}
            </button>
            {listResult && !importing && !importDone && (
              <button
                type="button"
                className="dock-demo-btn dock-demo-btn--primary"
                onClick={handleImport}
                disabled={listResult.files.length === 0 || !selectedMissionId}
              >
                Import {thermalCount + srtCount > 0 ? `(${thermalCount} photos + ${srtCount} SRT)` : ''}
              </button>
            )}
          </div>

          {listError && <div className="dock-demo-banner dock-demo-banner--err">{listError}</div>}

          {listResult && (
            <div className="dock-demo-summary">
              <div className="dock-demo-summary__row">
                <span><strong>{listResult.files.length}</strong> files · {formatBytes(totalBytes)}</span>
                {listResult.truncated && (
                  <span className="dock-demo-tag dock-demo-tag--warn">
                    truncated ({listResult.truncationReason})
                  </span>
                )}
              </div>
              <div className="dock-demo-summary__chips">
                {thermalCount > 0 && <span className="dock-demo-chip">{thermalCount} thermal</span>}
                {visualCount > 0 && <span className="dock-demo-chip">{visualCount} visual</span>}
                {zoomCount > 0 && <span className="dock-demo-chip">{zoomCount} zoom</span>}
                {videoCount > 0 && <span className="dock-demo-chip">{videoCount} video</span>}
                {srtCount > 0 && <span className="dock-demo-chip">{srtCount} SRT</span>}
                {listResult.flightPaths.length > 0 && (
                  <span className="dock-demo-chip">{listResult.flightPaths.length} flight path(s)</span>
                )}
              </div>
            </div>
          )}

          {importing && (
            <div className="dock-demo-banner">
              {importStep || 'Importing…'}
            </div>
          )}

          {importError && (
            <div className="dock-demo-banner dock-demo-banner--err">{importError}</div>
          )}

          {importDone && (
            <div className="dock-demo-banner dock-demo-banner--ok">
              Done. Imported data appears on the map. Close this dialog to review.
            </div>
          )}

          {importedPhotosCount > 0 && !importing && selectedMissionId && (
            <div className="dock-demo-modal__row">
              <button
                type="button"
                className="dock-demo-btn dock-demo-btn--ghost"
                onClick={() => clearMissionImportedData(selectedMissionId)}
              >
                Clear this mission's import ({importedPhotosCount} photos)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
